// P&P step tracking — the client half of backend/pp_tracking.py.
//
// Fernando (2026-09-07): every step of picking and packing is recorded so the
// Statistics can say where the time goes: notes printed, who is picking, list
// shown, grab-boxes, every Next/Prev, box swap, every button, every note /
// item / label scan, label print, packed, processed. This buffer takes those
// calls and flushes them in batches so a tablet never waits on the network.
//
// Contract: track() never throws and never blocks. Payloads carry codes and
// counts, never customer data. A batch the server did not take (restart, Wi-Fi
// blip) is KEPT — in memory and in localStorage — and sent again until it lands;
// every step carries its own id (`cid`) so a batch that did land but whose answer
// was lost is stored once (backend record_event, migration 074). Since 2026-10-05
// this matters: no order of a round is processed until every order's label scan
// has arrived (pp_tracking.round_guard). The server resolves the round from the order id
// and the actor from the device rule (a tablet's events are attributed to the
// person chosen on it).
import { authFetch, API } from '../api';

export type PpEventKind =
  | 'list_shown' | 'picking_started' | 'pick_next' | 'pick_prev' | 'box_swap'
  | 'box_swap_continue' | 'pick_done' | 'button'
  | 'note_scan' | 'item_scan' | 'note_rescan' | 'label_scan' | 'order_packed' | 'item_missing';

interface PpEvent {
  /** the step's own id — a resent batch is stored once */
  cid?: string;
  kind: PpEventKind;
  order_id?: string | null;
  round_id?: number | null;
  actor?: string | null;
  flow_key?: string | null;
  payload?: Record<string, unknown>;
  client_at: string;
}

const FLUSH_MS = 2000;
// 40 events ≈ 13 KB: well under the browser's 64 KB cap on a keepalive body (review 5.10: a 200-event
// retry backlog of ~66 KB was rejected by the browser and retried forever, label scans behind it)
const MAX_BATCH = 40;

let queue: PpEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
// Context set by the screens: the round being walked and who was chosen on a
// device. Sent with every event so a batch that arrives after the screen
// closed still lands on the right round.
let ctx: { roundId?: number | null; actor?: string | null; flowKey?: string | null; lang?: string | null } = {};

export function setTrackContext(c: { roundId?: number | null; actor?: string | null; flowKey?: string | null; lang?: string | null }) {
  ctx = { ...ctx, ...c };
}

/** The language of the person working (the packing instructions follow it), '' if unknown. */
export function trackLang(): string {
  return ctx.lang || '';
}

/** The person the screen is working as (a device's chosen picker/packer), null on a personal login. */
export function trackActor(): string | null {
  return ctx.actor || null;
}

export function currentFlowKey(): string {
  try { return localStorage.getItem('wms_pp_flow') || 'default'; } catch { return 'default'; }
}

// ── what the server has not confirmed yet: kept until it lands ─────────────────
const PENDING_KEY = 'wms_pp_pending';
const PENDING_MAX = 3000;                       // steps; the oldest go first past this
const RETRY_MS = [2000, 5000, 15000, 30000, 60000];
let pending: PpEvent[] = [];
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryStep = 0;
let sending = false;

function newCid(): string {
  try { if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID(); } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
function savePending() {
  try {
    if (pending.length > PENDING_MAX) pending = pending.slice(-PENDING_MAX);
    if (pending.length) localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    else localStorage.removeItem(PENDING_KEY);
  } catch { /* storage full or blocked: the in-memory copy still retries */ }
}
function keep(batch: PpEvent[]) {
  const have = new Set(pending.map(e => e.cid));
  for (const e of batch) if (!e.cid || !have.has(e.cid)) pending.push(e);
  savePending();
  scheduleRetry();
}
function settle(batch: PpEvent[]) {
  const done = new Set(batch.map(e => e.cid));
  pending = pending.filter(e => !done.has(e.cid));
  savePending();
}
function scheduleRetry(now = false) {
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  if (!pending.length) { retryStep = 0; return; }
  retryTimer = setTimeout(retryPending, now ? 0 : RETRY_MS[Math.min(retryStep, RETRY_MS.length - 1)]);
  retryStep += 1;
}
async function retryPending() {
  retryTimer = null;
  if (sending || !pending.length) return;
  sending = true;
  try {
    while (pending.length) {
      const batch = pending.slice(0, MAX_BATCH);
      if (!(await post(batch))) { scheduleRetry(); return; }
      settle(batch);
      retryStep = 0;
    }
  } finally { sending = false; }
}
/** true = the server took it (or refused it for good: a 4xx other than 401/408/429 never succeeds) */
async function post(batch: PpEvent[]): Promise<boolean> {
  try {
    const r = await authFetch(`${API}/pp/events`, { method: 'POST', body: JSON.stringify({ events: batch }), keepalive: true });
    if (r.ok) return true;
    return r.status >= 400 && r.status < 500 && ![401, 408, 429].includes(r.status);
  } catch { return false; }
}

function send(batch: PpEvent[], beacon = false): Promise<void> {
  if (!batch.length) return Promise.resolve();
  if (beacon && navigator.sendBeacon) {
    // sendBeacon cannot carry the bearer header, so the token rides IN THE BODY for this
    // one path — never in the URL, where it would land in every access log (2026-09-23).
    // A beacon has no answer: the batch is ALSO kept, and the next page load sends it again —
    // the server stores each step once (cid).
    const token = localStorage.getItem('wms_token') || '';
    keep(batch);
    try {
      navigator.sendBeacon(`${API}/pp/events`,
                           new Blob([JSON.stringify({ events: batch, token })], { type: 'application/json' }));
      return Promise.resolve();
    } catch { /* the kept copy is retried */ }
    return Promise.resolve();
  }
  return post(batch).then(ok => { if (!ok) keep(batch); });
}

/** Sends what is queued now. The promise settles when the server has answered (a fix processes an order right after
 *  its label scan, so its order_packed must be stored first — 2.11); a failed batch is kept for the retry as before. */
export function flushTracking(beacon = false): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  const batch = queue; queue = [];
  const sent: Promise<void>[] = [];
  while (batch.length) sent.push(send(batch.splice(0, MAX_BATCH), beacon));
  return Promise.all(sent).then(() => undefined);
}

export function track(kind: PpEventKind, payload?: Record<string, unknown>, orderId?: string | null) {
  try {
    queue.push({
      cid: newCid(),
      kind,
      order_id: orderId ?? null,
      round_id: ctx.roundId ?? null,
      actor: ctx.actor ?? null,
      flow_key: ctx.flowKey ?? currentFlowKey(),
      payload,
      client_at: new Date().toISOString(),
    });
    if (queue.length >= MAX_BATCH) flushTracking();
    else if (!timer) timer = setTimeout(() => flushTracking(), FLUSH_MS);
  } catch { /* never let tracking break the floor */ }
}

// Whatever is still buffered when the tab closes or goes to the background; and what an
// earlier page (or a failed POST) left unconfirmed is sent again when the page loads, when
// the network comes back and when the tab is in front again.
if (typeof window !== 'undefined') {
  try {
    const saved = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
    if (Array.isArray(saved) && saved.length) { pending = saved; scheduleRetry(true); }
  } catch { /* unreadable: start clean */ }
  window.addEventListener('pagehide', () => flushTracking(true));
  window.addEventListener('online', () => scheduleRetry(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTracking(true);
    else scheduleRetry(true);
  });
}
